// 审核员后台 HTTP 客户端封装。
// 开发默认指向本机 Laravel(http://wx.io/api/v1,hosts 已映射);
// 生产用 .env 的 VITE_API_BASE 覆盖为线上域名。
const BASE = import.meta.env.VITE_API_BASE ?? 'http://wx.io/api/v1';

const K_TOKEN = 'mba-reviewer:token';
const K_ADMIN = 'mba-reviewer:admin';

export interface AdminInfo {
  id: string;
  username: string;
  displayName: string | null;
  role: 'REVIEWER' | 'PLATFORM_ADMIN';
}

export interface ApiError {
  code: string;
  message: string;
  httpStatus: number;
}

export function getToken(): string | null {
  return localStorage.getItem(K_TOKEN);
}
export function setAuth(token: string, admin: AdminInfo): void {
  localStorage.setItem(K_TOKEN, token);
  localStorage.setItem(K_ADMIN, JSON.stringify(admin));
}
export function getAdmin(): AdminInfo | null {
  const raw = localStorage.getItem(K_ADMIN);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminInfo;
  } catch {
    return null;
  }
}
export function clearAuth(): void {
  localStorage.removeItem(K_TOKEN);
  localStorage.removeItem(K_ADMIN);
}

export async function request<T = unknown>(opts: {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  auth?: boolean;
}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.auth !== false) {
    const t = getToken();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }
  const res = await fetch(`${BASE}${opts.path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.ok) {
    return (await res.json()) as T;
  }
  let body: Partial<ApiError> = {};
  try {
    body = (await res.json()) as Partial<ApiError>;
  } catch {
    // ignore
  }
  if (res.status === 401) {
    clearAuth();
    // 让页面自行处理跳转
  }
  throw {
    code: body.code ?? `HTTP_${res.status}`,
    message: body.message ?? `请求失败(${res.status})`,
    httpStatus: res.status,
  } satisfies ApiError;
}

// ========== 具体业务 API ==========

export async function adminLogin(username: string, password: string) {
  const r = await request<{ token: string; admin: AdminInfo }>({
    method: 'POST',
    path: '/admin/auth/login',
    body: { username, password },
    auth: false,
  });
  setAuth(r.token, r.admin);
  return r.admin;
}

export async function adminMe(): Promise<AdminInfo> {
  return request<AdminInfo>({ method: 'GET', path: '/admin/auth/me' });
}

export interface CertListItem {
  id: string;
  certType: string;
  schoolName: string;
  enrollYear: number;
  fileUrls: string[];
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  user: {
    id: string;
    openid: string;
    displayName: string | null;
    status: string;
    createdAt: string;
  };
}

export async function listCertifications(status: 'PENDING' | 'APPROVED' | 'REJECTED' = 'PENDING') {
  return request<CertListItem[]>({
    method: 'GET',
    path: `/admin/audit/certifications?status=${status}`,
  });
}

// ========== 注册审核池:按用户列(含未交证件者) ==========

/** filter:PENDING/APPROVED/REJECTED = 最新证件状态;NONE = 从未提交;ALL = 全部 */
export type RegistrationFilter = 'PENDING' | 'APPROVED' | 'REJECTED' | 'NONE' | 'ALL';

export interface RegistrationItem {
  user: {
    id: string;
    openid: string;
    displayName: string | null;
    schoolName: string | null;
    enrollYear: number | null;
    status: string;
    createdAt: string;
    lastLoginAt: string | null;
  };
  latestCert: {
    id: string;
    certType: string;
    schoolName: string;
    enrollYear: number;
    fileUrls: string[];
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    rejectReason: string | null;
    createdAt: string;
    reviewedAt: string | null;
  } | null;
}

export async function listRegistrations(filter: RegistrationFilter = 'PENDING') {
  const qs = filter === 'ALL' ? '' : `?filter=${filter}`;
  return request<{ total: number; items: RegistrationItem[] }>({
    method: 'GET',
    path: `/admin/audit/registrations${qs}`,
  });
}

export async function approveCert(id: string) {
  return request<{ ok: true; userId: string }>({
    method: 'POST',
    path: `/admin/audit/certifications/${id}/approve`,
  });
}

export async function rejectCert(id: string, reason: string) {
  return request<{ ok: true; userId: string }>({
    method: 'POST',
    path: `/admin/audit/certifications/${id}/reject`,
    body: { reason },
  });
}

// ========== 身份叠加申请(P1-E) ==========

export interface RoleApplicationListItem {
  id: string;
  targetRole: 'MERCHANT' | 'TUTOR';
  applyReason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  user: {
    id: string;
    openid: string;
    displayName: string | null;
    schoolName: string | null;
    enrollYear: number | null;
    status: string;
  };
}

export async function listRoleApplications(
  status: 'PENDING' | 'APPROVED' | 'REJECTED' = 'PENDING',
) {
  return request<RoleApplicationListItem[]>({
    method: 'GET',
    path: `/admin/audit/role-applications?status=${status}`,
  });
}

export async function approveRoleApplication(id: string) {
  return request<{ ok: true; userId: string }>({
    method: 'POST',
    path: `/admin/audit/role-applications/${id}/approve`,
  });
}

export async function rejectRoleApplication(id: string, reason: string) {
  return request<{ ok: true; userId: string }>({
    method: 'POST',
    path: `/admin/audit/role-applications/${id}/reject`,
    body: { reason },
  });
}

// ========== 举报池(P2-A/D) ==========

export type ReportTargetType = 'MESSAGE' | 'POST' | 'COMMENT' | 'USER';
export type ReportStatus = 'PENDING' | 'RESOLVED' | 'DISMISSED';

export interface ReportListItem {
  id: string;
  targetType: ReportTargetType;
  targetId: string;
  reason: string;
  status: ReportStatus;
  createdAt: string;
  handledAt: string | null;
  reporter: {
    id: string;
    displayName: string | null;
  } | null;
}

export interface ReportTargetAuthor {
  id: string;
  displayName: string | null;
  avatarUrl?: string | null;
  schoolName?: string | null;
  enrollYear?: number | null;
  status?: string;
}

export type ReportTargetSnapshot =
  | {
      exists: false;
      type: ReportTargetType;
      unsupportedReason?: string;
    }
  | {
      exists: true;
      type: 'POST';
      status: string;
      text: string;
      createdAt: string;
      deletedAt: string | null;
      likeCount: number;
      commentCount: number;
      author: ReportTargetAuthor | null;
      media: Array<{ id: string; url: string; mimeType: string | null; sortOrder: number }>;
    }
  | {
      exists: true;
      type: 'COMMENT';
      text: string;
      createdAt: string;
      deletedAt: string | null;
      author: ReportTargetAuthor | null;
      post: {
        id: string;
        text: string;
        status: string;
        deletedAt: string | null;
        author: Pick<ReportTargetAuthor, 'id' | 'displayName'> | null;
      } | null;
    }
  | {
      exists: true;
      type: 'USER';
      id: string;
      displayName: string | null;
      avatarUrl: string | null;
      schoolName: string | null;
      enrollYear: number | null;
      status: string;
      createdAt: string;
      deletedAt: string | null;
    };

export interface ReportDetail extends ReportListItem {
  target: ReportTargetSnapshot;
}

export async function listReports(status: ReportStatus = 'PENDING') {
  return request<ReportListItem[]>({
    method: 'GET',
    path: `/admin/audit/reports?status=${status}`,
  });
}

export async function getReportDetail(id: string) {
  return request<ReportDetail>({
    method: 'GET',
    path: `/admin/audit/reports/${id}`,
  });
}

export async function resolveReport(id: string, takedown: boolean) {
  return request<{ ok: true; takedown: boolean }>({
    method: 'POST',
    path: `/admin/audit/reports/${id}/resolve`,
    body: { takedown },
  });
}

export async function dismissReport(id: string) {
  return request<{ ok: true }>({
    method: 'POST',
    path: `/admin/audit/reports/${id}/dismiss`,
  });
}

// ========== 广场机审池(P2-A) ==========

export interface PostAuthorBrief {
  id: string;
  displayName: string | null;
  avatarUrl: string | null;
  schoolName: string | null;
  enrollYear: number | null;
  status: string;
}

export interface PostMediaItem {
  id: string;
  url: string;
  mimeType: string | null;
  safetyLevel: string;
  safetyReason: string | null;
  sortOrder: number;
}

export interface AdminPostItem {
  id: string;
  text: string;
  visibleStatus: 'VISIBLE' | 'HIDDEN_PENDING_REVIEW' | 'BLOCKED';
  visibleScope: string;
  likeCount: number;
  commentCount: number;
  createdAt: string;
  author: PostAuthorBrief | null;
  media: PostMediaItem[];
}

export async function listPendingPosts() {
  return request<AdminPostItem[]>({
    method: 'GET',
    path: '/admin/audit/posts',
  });
}

export async function approvePost(id: string) {
  return request<{ ok: true; postId: string; from: string; to: string }>({
    method: 'POST',
    path: `/admin/audit/posts/${id}/approve`,
  });
}

export async function rejectPost(id: string, reason?: string) {
  return request<{ ok: true; postId: string; from: string; to: string }>({
    method: 'POST',
    path: `/admin/audit/posts/${id}/reject`,
    body: reason ? { reason } : undefined,
  });
}

// ========== M友名片修改审核(内容审核池,RFC-003) ==========

export interface DirectoryCardSnapshot {
  realName: string | null;
  industry: string | null;
  industryCustomTag: string | null;
  company: string | null;
  title: string | null;
  bio: string | null;
  directoryVisible: boolean;
  contact: {
    wechatId: string | null;
    mobile: string | null;
    email: string | null;
    note: string | null;
  };
}

export interface DirectoryChangeItem {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  snapshot: DirectoryCardSnapshot;
  user: { id: string; displayName: string | null; schoolName: string | null } | null;
}

export async function listDirectoryChanges(
  status: 'PENDING' | 'APPROVED' | 'REJECTED' = 'PENDING',
) {
  return request<DirectoryChangeItem[]>({
    method: 'GET',
    path: `/admin/audit/directory-changes?status=${status}`,
  });
}

export async function approveDirectoryChange(id: string) {
  return request<{ ok: true; userId: string }>({
    method: 'POST',
    path: `/admin/audit/directory-changes/${id}/approve`,
  });
}

export async function rejectDirectoryChange(id: string, reason: string) {
  return request<{ ok: true; userId: string }>({
    method: 'POST',
    path: `/admin/audit/directory-changes/${id}/reject`,
    body: { reason },
  });
}

// ========== 部落创建审核池(RFC-008) ==========

export interface TribePendingItem {
  id: string;
  name: string;
  intro: string | null;
  coverUrl: string | null;
  creatorId: string;
  creatorName: string | null;
  creatorSchool: string | null;
  createdAt: string;
}

export async function listPendingTribes() {
  return request<{ items: TribePendingItem[] }>({
    method: 'GET',
    path: '/admin/audit/tribes',
  });
}

export async function approveTribe(id: string) {
  return request<{ ok: true }>({ method: 'POST', path: `/admin/audit/tribes/${id}/approve` });
}

export async function rejectTribe(id: string, reason: string) {
  return request<{ ok: true }>({
    method: 'POST',
    path: `/admin/audit/tribes/${id}/reject`,
    body: { reason },
  });
}

/** 证件图可用完整 URL(带 token 作为 query 无法,管理员 token 走 Authorization header 不变) */
export function imageUrl(path: string): string {
  return `${BASE.replace(/\/api\/v1$/, '')}${path}`;
}

/** 证件图拉 blob,注入 Authorization,然后 createObjectURL 供 <img> 渲染 */
export async function fetchImageBlob(path: string): Promise<string> {
  const t = getToken();
  const res = await fetch(imageUrl(path), {
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
  if (!res.ok) throw new Error(`图加载失败:${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
